import React, { useState, useEffect, useCallback, useRef } from "react";
import { getJson, postJson } from "../../services/http";

// ── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL = 2000; // ms

const COMPONENT_COLORS = {
  Auth:          "#6f42c1",
  Tenants:       "#0d6efd",
  Users:         "#0dcaf0",
  Events:        "#198754",
  Communication: "#fd7e14",
  Workflow:      "#ffc107",
  Content:       "#d63384",
  Reporting:     "#20c997",
  Configuration: "#6c757d",
};

const STATUS_BADGE = {
  passed:   { bg: "#198754", label: "Passed" },
  failed:   { bg: "#dc3545", label: "Failed" },
  skipped:  { bg: "#6c757d", label: "Skipped" },
  timedOut: { bg: "#fd7e14", label: "Timed Out" },
  running:  { bg: "#0d6efd", label: "Running" },
  error:    { bg: "#dc3545", label: "Error" },
};

// ── Component ────────────────────────────────────────────────────────────────

export default function TestRunner({ tenantId }) {
  const [authorized, setAuthorized] = useState(false);
  const [suites, setSuites] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [groupBy, setGroupBy] = useState("component"); // 'component' | 'type'
  const [activeRun, setActiveRun] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [expandedSuites, setExpandedSuites] = useState(new Set());
  const pollRef = useRef(null);

  // ── Auth check ────────────────────────────────────────────────────────────

  useEffect(() => {
    getJson("/api/super-admin/check-access")
      .then(() => setAuthorized(true))
      .catch(() => setAuthorized(false));
  }, []);

  // ── Load suites ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!authorized) return;
    getJson("/api/test-runner/suites").then((data) => {
      setSuites(data.suites || []);
      setSelected(new Set((data.suites || []).map((s) => s.id)));
    });
    getJson("/api/test-runner/runs").then((data) => {
      setRunHistory(data.runs || []);
    });
  }, [authorized]);

  // ── Polling for active run ────────────────────────────────────────────────

  const pollRun = useCallback((runId) => {
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = () => {
      getJson(`/api/test-runner/runs/${runId}`).then((data) => {
        setActiveRun(data.run);
        if (data.run.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Refresh history
          getJson("/api/test-runner/runs").then((h) => setRunHistory(h.runs || []));
        }
      });
    };

    poll(); // immediate first fetch
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Run tests ─────────────────────────────────────────────────────────────

  const handleRun = async () => {
    const suiteIds = [...selected];
    if (suiteIds.length === 0) return;

    try {
      const data = await postJson("/api/test-runner/run", { suiteIds });
      if (data.runId) {
        setActiveRun({ id: data.runId, status: "running", suiteIds, startedAt: new Date().toISOString() });
        setExpandedErrors(new Set());
        setExpandedSuites(new Set());
        pollRun(data.runId);
      }
    } catch (err) {
      console.error("Failed to start test run:", err);
    }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggleSuite = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(suites.map((s) => s.id)));
  const selectNone = () => setSelected(new Set());

  const selectGroup = (key) => {
    const field = groupBy === "component" ? "component" : "type";
    const ids = suites.filter((s) => s[field] === key).map((s) => s.id);
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  // ── Grouping ──────────────────────────────────────────────────────────────

  const grouped = {};
  const field = groupBy === "component" ? "component" : "type";
  for (const s of suites) {
    const key = s[field] || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  }

  // ── Toggle helpers ────────────────────────────────────────────────────────

  const toggleError = (idx) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const toggleSuiteExpand = (suite) => {
    setExpandedSuites((prev) => {
      const next = new Set(prev);
      next.has(suite) ? next.delete(suite) : next.add(suite);
      return next;
    });
  };

  // ── Reload a past run ─────────────────────────────────────────────────────

  const loadRun = (runId) => {
    getJson(`/api/test-runner/runs/${runId}`).then((data) => {
      setActiveRun(data.run);
      setExpandedErrors(new Set());
      setExpandedSuites(new Set());
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!authorized) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">
          <i className="bi bi-shield-exclamation me-2"></i>
          Super admin access required to use the Test Runner.
        </div>
      </div>
    );
  }

  const isRunning = activeRun?.status === "running";

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h3 className="mb-1">
            <i className="bi bi-bug me-2"></i>Playwright Test Runner
          </h3>
          <small className="text-muted">Regression test suite for OnScreen PWA</small>
        </div>
        <div className="d-flex gap-2">
          <div className="btn-group btn-group-sm">
            <button
              className={`btn ${groupBy === "component" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setGroupBy("component")}
            >
              By Component
            </button>
            <button
              className={`btn ${groupBy === "type" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setGroupBy("type")}
            >
              By Type
            </button>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* ── Left: Suite selection ────────────────────────────────────────── */}
        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Test Suites</strong>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary btn-sm" onClick={selectAll}>All</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={selectNone}>None</button>
              </div>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 500, overflowY: "auto" }}>
              {Object.entries(grouped).map(([groupName, items]) => {
                const allChecked = items.every((s) => selected.has(s.id));
                const someChecked = items.some((s) => selected.has(s.id));
                return (
                  <div key={groupName}>
                    {/* Group header */}
                    <div
                      className="d-flex align-items-center px-3 py-2"
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #dee2e6",
                        cursor: "pointer",
                      }}
                      onClick={() => selectGroup(groupName)}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={allChecked}
                        ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                        readOnly
                      />
                      <span
                        className="badge me-2"
                        style={{ backgroundColor: COMPONENT_COLORS[groupName] || "#6c757d" }}
                      >
                        {groupName}
                      </span>
                      <small className="text-muted ms-auto">{items.length} suite{items.length !== 1 ? "s" : ""}</small>
                    </div>
                    {/* Suite items */}
                    {items.map((suite) => (
                      <div
                        key={suite.id}
                        className="d-flex align-items-center px-3 py-2"
                        style={{
                          borderBottom: "1px solid #f0f0f0",
                          paddingLeft: 28,
                          cursor: "pointer",
                          backgroundColor: selected.has(suite.id) ? "#f0f7ff" : "transparent",
                        }}
                        onClick={() => toggleSuite(suite.id)}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selected.has(suite.id)}
                          readOnly
                        />
                        <span>{suite.name}</span>
                        <small className="text-muted ms-auto">{suite.id}</small>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="card-footer">
              <button
                className="btn btn-success w-100"
                onClick={handleRun}
                disabled={isRunning || selected.size === 0}
              >
                {isRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Running {activeRun.suiteIds?.length || 0} suite(s)...
                  </>
                ) : (
                  <>
                    <i className="bi bi-play-fill me-1"></i>
                    Run {selected.size} Suite{selected.size !== 1 ? "s" : ""}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Run history ─────────────────────────────────────────────── */}
          {runHistory.length > 0 && (
            <div className="card shadow-sm mt-3">
              <div className="card-header"><strong>Run History</strong></div>
              <div className="list-group list-group-flush" style={{ maxHeight: 250, overflowY: "auto" }}>
                {runHistory.map((r) => (
                  <button
                    key={r.id}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${activeRun?.id === r.id ? "active" : ""}`}
                    onClick={() => loadRun(r.id)}
                  >
                    <div>
                      <small>{new Date(r.startedAt).toLocaleTimeString()}</small>
                      <br />
                      <small className="text-muted">{r.suiteIds?.length || 0} suites</small>
                    </div>
                    <span
                      className="badge"
                      style={{ backgroundColor: STATUS_BADGE[r.status]?.bg || "#6c757d" }}
                    >
                      {STATUS_BADGE[r.status]?.label || r.status}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Results ──────────────────────────────────────────────── */}
        <div className="col-lg-8">
          {!activeRun ? (
            <div className="card shadow-sm">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-play-circle" style={{ fontSize: 48 }}></i>
                <p className="mt-3 mb-0">Select test suites and click <strong>Run</strong> to start.</p>
                <p className="mt-1">
                  <small>Tests can also be run via CLI: <code>npm test</code> or <code>npm run test:events</code></small>
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Summary card */}
              <div className="card shadow-sm mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <span
                        className="badge me-2"
                        style={{ backgroundColor: STATUS_BADGE[activeRun.status]?.bg || "#6c757d" }}
                      >
                        {STATUS_BADGE[activeRun.status]?.label || activeRun.status}
                      </span>
                      Run Results
                    </h5>
                    {activeRun.finishedAt && (
                      <small className="text-muted">
                        {Math.round((new Date(activeRun.finishedAt) - new Date(activeRun.startedAt)) / 1000)}s total
                      </small>
                    )}
                  </div>

                  {isRunning && (
                    <div className="progress mb-3" style={{ height: 4 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        style={{ width: "100%" }}
                      />
                    </div>
                  )}

                  {activeRun.summary && (
                    <div className="row text-center g-2">
                      <div className="col">
                        <div className="border rounded p-2">
                          <div style={{ fontSize: 24, fontWeight: 700 }}>{activeRun.summary.total}</div>
                          <small className="text-muted">Total</small>
                        </div>
                      </div>
                      <div className="col">
                        <div className="border rounded p-2" style={{ borderColor: "#198754 !important" }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#198754" }}>{activeRun.summary.passed}</div>
                          <small className="text-muted">Passed</small>
                        </div>
                      </div>
                      <div className="col">
                        <div className="border rounded p-2">
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#dc3545" }}>{activeRun.summary.failed}</div>
                          <small className="text-muted">Failed</small>
                        </div>
                      </div>
                      <div className="col">
                        <div className="border rounded p-2">
                          <div style={{ fontSize: 24, fontWeight: 700, color: "#6c757d" }}>{activeRun.summary.skipped}</div>
                          <small className="text-muted">Skipped</small>
                        </div>
                      </div>
                      <div className="col">
                        <div className="border rounded p-2">
                          <div style={{ fontSize: 24, fontWeight: 700 }}>
                            {activeRun.summary.duration ? `${(activeRun.summary.duration / 1000).toFixed(1)}s` : "-"}
                          </div>
                          <small className="text-muted">Duration</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Test results list */}
              {activeRun.tests && activeRun.tests.length > 0 && (
                <div className="card shadow-sm">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Test Details</strong>
                    <small className="text-muted">{activeRun.tests.length} test(s)</small>
                  </div>
                  <div className="card-body p-0">
                    {renderTestsBySuite(activeRun.tests)}
                  </div>
                </div>
              )}

              {/* stderr / raw errors */}
              {activeRun.rawStderr && activeRun.status !== "running" && (
                <div className="card shadow-sm mt-3">
                  <div className="card-header"><strong>Raw Output</strong></div>
                  <div className="card-body">
                    <pre
                      style={{
                        maxHeight: 300,
                        overflow: "auto",
                        fontSize: 12,
                        backgroundColor: "#1e1e1e",
                        color: "#d4d4d4",
                        padding: 12,
                        borderRadius: 6,
                        margin: 0,
                      }}
                    >
                      {activeRun.rawStderr}
                    </pre>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Render tests grouped by suite name ──────────────────────────────────

  function renderTestsBySuite(tests) {
    const bySuite = {};
    for (const t of tests) {
      const key = t.suite || "Unknown";
      if (!bySuite[key]) bySuite[key] = [];
      bySuite[key].push(t);
    }

    return Object.entries(bySuite).map(([suiteName, suiteTests]) => {
      const passed = suiteTests.filter((t) => t.status === "passed").length;
      const failed = suiteTests.filter((t) => t.status === "failed" || t.status === "timedOut").length;
      const isExpanded = expandedSuites.has(suiteName);
      const hasFailures = failed > 0;

      return (
        <div key={suiteName}>
          {/* Suite header */}
          <div
            className="d-flex align-items-center px-3 py-2"
            style={{
              backgroundColor: hasFailures ? "#fff5f5" : "#f8fff8",
              borderBottom: "1px solid #dee2e6",
              cursor: "pointer",
            }}
            onClick={() => toggleSuiteExpand(suiteName)}
          >
            <i className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}></i>
            <strong className="me-2" style={{ fontSize: 13 }}>{suiteName}</strong>
            <span className="badge bg-success me-1">{passed}</span>
            {failed > 0 && <span className="badge bg-danger me-1">{failed}</span>}
            <small className="text-muted ms-auto">
              {suiteTests.length} test{suiteTests.length !== 1 ? "s" : ""}
            </small>
          </div>

          {/* Test rows */}
          {(isExpanded || hasFailures) &&
            suiteTests.map((t, idx) => {
              const globalIdx = tests.indexOf(t);
              const isFail = t.status === "failed" || t.status === "timedOut";
              const isErrorExpanded = expandedErrors.has(globalIdx);

              return (
                <div key={idx}>
                  <div
                    className="d-flex align-items-center px-3 py-2"
                    style={{
                      paddingLeft: 36,
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: isFail ? "#fff0f0" : "transparent",
                      cursor: isFail ? "pointer" : "default",
                    }}
                    onClick={() => isFail && toggleError(globalIdx)}
                  >
                    <i
                      className={`bi me-2 ${
                        t.status === "passed"
                          ? "bi-check-circle-fill text-success"
                          : t.status === "skipped"
                          ? "bi-dash-circle text-secondary"
                          : "bi-x-circle-fill text-danger"
                      }`}
                    ></i>
                    <span style={{ fontSize: 13 }}>{t.title}</span>
                    <small className="text-muted ms-auto">
                      {t.duration ? `${(t.duration / 1000).toFixed(2)}s` : ""}
                    </small>
                    {isFail && (
                      <i className={`bi ${isErrorExpanded ? "bi-chevron-up" : "bi-chevron-down"} ms-2 text-danger`}></i>
                    )}
                  </div>

                  {/* Error details */}
                  {isFail && isErrorExpanded && t.error && (
                    <div
                      style={{
                        backgroundColor: "#1e1e1e",
                        color: "#f48771",
                        padding: "12px 16px 12px 48px",
                        fontSize: 12,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      <div style={{ color: "#f48771", fontWeight: 600, marginBottom: 4 }}>
                        {t.error}
                      </div>
                      {t.errorStack && (
                        <div style={{ color: "#808080", marginTop: 8 }}>
                          {t.errorStack}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      );
    });
  }
}
